import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import t from "@/lang/lang";
import { ReactNode } from "react";
import { DocumentNumberById } from "@/services/backend/numbers/components/document-number";
import { formatDate, formatTime } from "@/shared/lib/utilities/date";
import { Button } from "@/shared/components/ui/button";
import { Search } from "lucide-react";
import {
  ListRequest,
  useHtzReportListQuery,
} from "@/services/backend/htz/report/api/report-api";
import { Report } from "@/services/backend/htz/report/types/report";
import { TableCard } from "@/shared/components/tables/table-card";
import { TableLoadingRow } from "@/shared/components/tables/table-loading-row";
import { TableRtkErrorRow } from "@/shared/components/tables/table-rtk-error-row";
import { TableEmptyRow } from "@/shared/components/tables/table-empty-row";
import { PaginationLimitOffset } from "@/shared/components/tables/pagination-limit-offset";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";

export function ReportsTable({
  request,
  onRequestChange,
  renderRowLink,
  hidePagination = false,
  hideBorder = false,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
  renderRowLink: (reportId: string, children: ReactNode) => ReactNode;
  hidePagination?: boolean;
  hideBorder?: boolean;
}) {
  const { data: list, isLoading, error } = useHtzReportListQuery(request);

  const emptyList = !isLoading && !error && list!.items.length === 0;

  return (
    <div className="grid gap-2">
      <TableCard className={hideBorder ? "border-none shadow-none" : ""}>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t("Nummer")}</TableHead>
              <TableHead>{t("Erstellt am")}</TableHead>
              <TableHead className="w-full">{t("Festgeschrieben")}</TableHead>
              <TableHead className="w-32">{t("Aktionen")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && <TableLoadingRow colSpan={4} />}
            {error && <TableRtkErrorRow error={error} colSpan={4} />}
            {emptyList && <TableEmptyRow colSpan={4} />}
            {list &&
              list.items.map((report) => (
                <ReportTableRow
                  key={report.id}
                  report={report}
                  renderRowLink={renderRowLink}
                />
              ))}
          </TableBody>
        </Table>
      </TableCard>
      {!hidePagination && list && !emptyList && (
        <PaginationLimitOffset
          offset={request.offset ?? 0}
          limit={request.limit ?? 0}
          totalCount={list.totalCount}
          setLimitOffset={(limit, offset) =>
            onRequestChange({ ...request, limit, offset })
          }
          className="ml-auto"
        />
      )}
    </div>
  );
}

function ReportTableRow({
  report,
  renderRowLink,
}: {
  report: Report;
  renderRowLink: (reportId: string, children: ReactNode) => ReactNode;
}) {
  return (
    <TableRow>
      <TableCell>
        {renderRowLink(
          report.id,
          <DocumentNumberById documentId={report.id} />,
        )}
      </TableCell>
      <TableCell className="whitespace-nowrap">
        {formatDate(report.date ?? "")}, {formatTime(report.date ?? "")}
      </TableCell>
      <TableCell>{report.immutable ? "ja" : "nein"}</TableCell>
      <TableCell>
        <div className="flex space-x-2">
          {renderRowLink(
            report.id,
            <Button variant="ghost">
              <Search className="h-4 w-4" />
            </Button>,
          )}
        </div>
      </TableCell>
    </TableRow>
  );
}

export function ReportTableFilters({
  request,
  onRequestChange,
}: {
  request: ListRequest;
  onRequestChange: (request: ListRequest) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <OrderBySelect<string>
        orderBy={request.orderBy}
        onOrderByChange={(orderBy) => onRequestChange({ ...request, orderBy })}
        fields={[
          { label: t("Datum"), value: "date" },
          { label: t("Inspektionsdatum"), value: "inspectionDate" },
          { label: t("Geprüft von"), value: "inspectionConductedBy" },
          { label: t("Nächste Inspektion"), value: "nextInspectionDate" },
          { label: t("VDI 6022"), value: "vdi6022" },
          { label: t("Erstellt am"), value: "createdAt" },
          { label: t("Aktualisiert am"), value: "updatedAt" },
          { label: t("Festgeschrieben am"), value: "immutabilitySetAt" },
        ]}
      />
    </div>
  );
}

export function useDefaultHtzReportListRequest(processId: string): ListRequest {
  return {
    process: { active: true, values: [processId] },
    customer: { active: false, values: [] },
    immutable: { active: false, values: [] },
    orderBy: [{ field: "date" }],
    limit: 50,
    offset: 0,
  };
}
